"use client"

import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from '../../lib/utils'
import { UIBox } from '../layout/ui-box'

const skeletonVariants = cva(
  "bg-skeleton border border-border block",
  {
    variants: {
      variant: {
        text: "rounded-sm h-4",
        circular: "rounded-full aspect-square",
        rectangular: "rounded-none",
        rounded: "rounded-md",
      },
      animation: {
        pulse: "animate-pulse",
        none: "animate-none",
      },
      size: {
        sm: "",
        default: "",
        lg: "",
      },
    },
    defaultVariants: {
      variant: "text",
      animation: "pulse",
      size: "default",
    },
  }
)

export interface UISkeletonProps extends VariantProps<typeof skeletonVariants> {
  /**
   * 추가 CSS 클래스
   */
  className?: string
  /**
   * 너비 설정 (CSS 값 또는 숫자)
   */
  width?: string | number
  /**
   * 높이 설정 (CSS 값 또는 숫자)
   */
  height?: string | number
  /**
   * 텍스트 skeleton용 줄 수
   */
  lines?: number
  /**
   * 줄 간격 (rem 단위)
   */
  lineSpacing?: number
  /**
   * 인라인 스타일 객체
   */
  style?: React.CSSProperties
  /**
   * aria-label for accessibility
   */
  "aria-label"?: string
}

/**
 * UISkeleton - 고성능 로딩 상태 컴포넌트
 *
 * 다양한 형태와 애니메이션을 지원하는 skeleton 컴포넌트입니다.
 * 성능 최적화된 CSS 애니메이션을 사용하며 접근성을 완전히 지원합니다.
 *
 * @example
 * ```tsx
 * // 기본 텍스트 skeleton
 * <UISkeleton width="200px" height="20px" />
 *
 * // 여러 줄 텍스트
 * <UISkeleton variant="text" lines={3} />
 *
 * // 원형 아바타
 * <UISkeleton variant="circular" width="40px" height="40px" />
 *
 * // 애니메이션 없음
 * <UISkeleton animation="none" width="100%" height="100px" />
 * ```
 */
export const UISkeleton = React.forwardRef<
  HTMLDivElement,
  UISkeletonProps
>(({
  className,
  variant = "text",
  animation = "pulse",
  size = "default",
  width,
  height,
  lines = 1,
  lineSpacing = 0.5,
  style,
  "aria-label": ariaLabel,
  ...props
}, ref) => {
  // 스타일 객체 생성
  const skeletonStyle: React.CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
    ...style,
  }

  // 단일 skeleton 렌더링
  if (lines === 1) {
    return (
      <UIBox
        ref={ref}
        className={cn(
          skeletonVariants({ variant, animation, size }),
          className
        )}
        style={skeletonStyle}
        role="status"
        aria-label={ariaLabel || "로딩 중..."}
        aria-live="polite"
        {...props}
      />
    )
  }

  // 여러 줄 skeleton 렌더링
  return (
    <UIBox
      ref={ref}
      className={cn("space-y-2", className)}
      role="status"
      aria-label={ariaLabel || `${lines}줄 로딩 중...`}
      aria-live="polite"
      {...props}
    >
      {Array.from({ length: lines }, (_, index) => {
        // 마지막 줄은 80% 너비로 더 자연스럽게
        const isLastLine = index === lines - 1
        const lineWidth = isLastLine && lines > 1 ? "80%" : width || "100%"

        return (
          <UIBox
            key={index}
            className={cn(
              skeletonVariants({ variant, animation, size })
            )}
            style={{
              width: typeof lineWidth === 'number' ? `${lineWidth}px` : lineWidth,
              height: typeof height === 'number' ? `${height}px` : height,
              marginBottom: index < lines - 1 ? `${lineSpacing}rem` : 0,
            }}
          />
        )
      })}
    </UIBox>
  )
})

UISkeleton.displayName = "UISkeleton"

// Convenience components for common patterns
export const UISkeletonText = React.forwardRef<
  HTMLDivElement,
  Omit<UISkeletonProps, 'variant'>
>((props, ref) => {
  return <UISkeleton ref={ref} variant="text" {...props} />
})

UISkeletonText.displayName = "UISkeletonText"

export const UISkeletonCircular = React.forwardRef<
  HTMLDivElement,
  Omit<UISkeletonProps, 'variant'>
>((props, ref) => {
  return <UISkeleton ref={ref} variant="circular" {...props} />
})

UISkeletonCircular.displayName = "UISkeletonCircular"

export const UISkeletonRectangular = React.forwardRef<
  HTMLDivElement,
  Omit<UISkeletonProps, 'variant'>
>((props, ref) => {
  return <UISkeleton ref={ref} variant="rectangular" {...props} />
})

UISkeletonRectangular.displayName = "UISkeletonRectangular"
